import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorMessage from '../../components/ui/ErrorMessage';
import SuccessMessage from '../../components/ui/SuccessMessage';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import QuestionFormModal from '../../components/admin/QuestionFormModal';
import mcqService from '../../services/mcqService';

/**
 * ManageQuestions - Admin page for managing questions for a specific MCQ
 * Displays all questions with options to create, edit, and delete
 */
const ManageQuestions = () => {
  const { mcqId } = useParams();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [mcq, setMcq] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, questionId: null, questionText: '' });

  // Redirect if not admin
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user && user.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch MCQ and questions on mount
  useEffect(() => {
    if (user && user.role === 'admin' && mcqId) {
      fetchMCQAndQuestions();
    }
  }, [user, mcqId]);

  /**
   * Fetch MCQ details and questions
   */
  const fetchMCQAndQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch MCQ details
      const mcqData = await mcqService.getMCQById(mcqId);
      setMcq(mcqData);

      // Fetch questions for this MCQ
      const questionsData = await mcqService.getQuestionsByMCQ(mcqId);
      setQuestions(questionsData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.detail || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Open modal for creating a new question
   */
  const handleCreateQuestion = () => {
    setEditingQuestion(null);
    setIsModalOpen(true);
  };

  /**
   * Open modal for editing an existing question
   */
  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setIsModalOpen(true);
  };

  /**
   * Open delete confirmation dialog
   */
  const handleDeleteClick = (question) => {
    setDeleteDialog({
      isOpen: true,
      questionId: question.id,
      questionText: question.question_text
    });
  };

  /**
   * Delete a question after confirmation
   */
  const handleDeleteConfirm = async () => {
    try {
      await mcqService.deleteQuestion(deleteDialog.questionId);
      setSuccessMessage('Question deleted successfully!');
      setDeleteDialog({ isOpen: false, questionId: null, questionText: '' });
      // Refresh questions list
      fetchMCQAndQuestions();
    } catch (err) {
      console.error('Failed to delete question:', err);
      setError(err.response?.data?.detail || 'Failed to delete question. Please try again.');
    }
  };

  /**
   * Handle successful question save (create or update)
   */
  const handleQuestionSaved = () => {
    setIsModalOpen(false);
    setEditingQuestion(null);
    setSuccessMessage(editingQuestion ? 'Question updated successfully!' : 'Question created successfully!');
    // Refresh questions list
    fetchMCQAndQuestions();
  };

  /**
   * Get option label badge color
   */
  const getOptionBadgeColor = (option, correctAnswer) => {
    if (option === correctAnswer) {
      return 'bg-green-100 text-green-800 border-green-300';
    }
    return 'bg-gray-100 text-gray-700 border-gray-300';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow">
          <LoadingSpinner message="Loading questions..." />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate(-1)}
                className="text-blue-600 hover:text-blue-800 mb-2 flex items-center gap-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to MCQs
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                Question Management: {mcq?.title}
              </h1>
              <p className="text-gray-600 mt-1">Add and manage questions for this MCQ</p>
            </div>
            <button
              onClick={handleCreateQuestion}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl font-semibold"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Question
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <SuccessMessage message={successMessage} onClose={() => setSuccessMessage('')} />
        )}
        {error && (
          <ErrorMessage message={error} onRetry={fetchMCQAndQuestions} />
        )}

        {/* Questions List */}
        {questions.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow">
            <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Questions Yet</h3>
            <p className="text-gray-500 mb-6">Get started by creating your first question</p>
            <button
              onClick={handleCreateQuestion}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First Question
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {questions.map((question, index) => (
              <div key={question.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                {/* Question Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                        Question {index + 1}
                      </span>
                      <span className="text-sm text-gray-500">Order: {question.order}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{question.question_text}</h3>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEditQuestion(question)}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                      title="Edit Question"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteClick(question)}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                      title="Delete Question"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {['A', 'B', 'C', 'D', 'E'].map(option => {
                    const optionValue = question[`option_${option.toLowerCase()}`];

                    // Skip if option E is empty (for backward compatibility)
                    if (option === 'E' && (!optionValue || optionValue.trim() === '')) {
                      return null;
                    }

                    return (
                      <div
                        key={option}
                        className={`p-3 border-2 rounded-lg ${getOptionBadgeColor(option, question.correct_answer)}`}
                      >
                        <div className="flex items-start gap-2">
                          <span className="font-bold flex-shrink-0">{option}.</span>
                          <span className="flex-grow">{optionValue}</span>
                          {option === question.correct_answer && (
                            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Explanation */}
                {question.explanation && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 mb-1">Explanation:</p>
                    <p className="text-sm text-blue-800">{question.explanation}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />

      {/* Question Form Modal */}
      <QuestionFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingQuestion(null);
        }}
        onSave={handleQuestionSaved}
        question={editingQuestion}
        mcqId={mcqId}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title="Delete Question"
        message={`Are you sure you want to delete this question? This action cannot be undone.`}
        confirmText="Delete"
        confirmStyle="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, questionId: null, questionText: '' })}
      />
    </div>
  );
};

export default ManageQuestions;
